import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, type AdminUser } from './api'
import Pager from './Pager'
import PasswordField from './PasswordField'

const inputClass = 'w-full border border-rule bg-paper px-3 py-2.5 text-[15px] outline-none focus:border-ink'
const labelClass = 'text-caps mb-1.5 block text-[10px] text-muted'
const linkButton = 'text-[11px] tracking-[0.16em] text-muted uppercase hover:text-rose'

const ROLE_HELP = {
  admin: 'Everything, including statistics, contributors, the team and logs.',
  editor: 'Write, edit and schedule posts. Cannot delete posts or open admin pages.',
} as const

type Role = keyof typeof ROLE_HELP

function AddAdmin({ onAdded }: { onAdded: (email: string) => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('editor')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api('/api/admin/users', { method: 'POST', body: JSON.stringify({ name, email, password, role }) })
      onAdded(email.trim().toLowerCase())
      setName('')
      setEmail('')
      setPassword('')
      setRole('editor')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that admin.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="mt-6 max-w-[520px] border border-rule bg-paper/40 p-5">
      <h3 className="text-caps text-[11px] text-muted">Add someone</h3>
      <p className="mt-2 text-sm text-muted">
        They sign in at /admin with this email and password. Send them the password yourself; they can change it under
        Account.
      </p>
      {error ? <p className="mt-4 text-sm text-rose">{error}</p> : null}
      <div className="mt-5 flex flex-col gap-5">
        <label className="block">
          <span className={labelClass}>Name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass} />
        </label>
        <label className="block">
          <span className={labelClass}>Email</span>
          <input
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClass}
          />
        </label>
        <PasswordField label="Password" value={password} onChange={setPassword} generate />
        <fieldset>
          <legend className={labelClass}>Role</legend>
          <div className="flex flex-col gap-2">
            {(Object.keys(ROLE_HELP) as Role[]).map((value) => (
              <label key={value} className="flex cursor-pointer items-start gap-2.5 text-[14px]">
                <input
                  type="radio"
                  name="role"
                  value={value}
                  checked={role === value}
                  onChange={() => setRole(value)}
                  className="mt-1 accent-[var(--color-rose)]"
                />
                <span>
                  <span className="capitalize">{value}</span>
                  <span className="block text-[12px] text-muted">{ROLE_HELP[value]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <button type="submit" disabled={busy} className="text-caps mt-6 h-11 w-full bg-ink text-white hover:bg-rose disabled:opacity-60">
        {busy ? 'Adding…' : `Add ${role}`}
      </button>
    </form>
  )
}

function ResetPassword({ user, onDone }: { user: AdminUser; onDone: (message: string) => void }) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api('/api/admin/users/password', { method: 'PUT', body: JSON.stringify({ email: user.email, password }) })
      onDone(`New password saved for ${user.email}. They've been signed out everywhere.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that password.')
      setBusy(false)
    }
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="mt-3 max-w-[420px]">
      {error ? <p className="mb-3 text-sm text-rose">{error}</p> : null}
      <PasswordField label={`New password for ${user.name || user.email}`} value={password} onChange={setPassword} generate />
      <button type="submit" disabled={busy} className="text-caps mt-4 h-10 bg-ink px-5 text-white hover:bg-rose disabled:opacity-60">
        {busy ? 'Saving…' : 'Save password'}
      </button>
    </form>
  )
}

const PER_PAGE = 15
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'admin', label: 'Admins' },
  { key: 'editor', label: 'Editors' },
] as const

function RoleSwitch({ user, onChange }: { user: AdminUser; onChange: (role: Role) => void }) {
  // You can't change your own role, and the owner is always an admin.
  const locked = user.you || user.owner
  return (
    <div
      role="group"
      aria-label={`Role for ${user.email}`}
      title={locked ? (user.owner ? 'The owner is always an admin' : "You can't change your own role") : undefined}
      className={`inline-flex border border-rule text-[10px] ${locked ? 'opacity-60' : ''}`}
    >
      {(['admin', 'editor'] as Role[]).map((value) => (
        <button
          key={value}
          type="button"
          disabled={locked || user.role === value}
          aria-pressed={user.role === value}
          onClick={() => onChange(value)}
          className={`text-caps h-7 px-2.5 transition-colors ${
            user.role === value ? 'bg-ink text-white' : 'text-muted hover:bg-sand hover:text-ink disabled:hover:bg-transparent'
          }`}
        >
          {value}
        </button>
      ))}
    </div>
  )
}

export default function AdminTeam() {
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [resetting, setResetting] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const load = useCallback(() => {
    api<AdminUser[]>('/api/admin/users')
      .then(setUsers)
      .catch((err: Error) => setError(err.message))
  }, [])

  useEffect(load, [load])

  async function changeRole(user: AdminUser, role: Role) {
    const who = user.name || user.email
    if (role === 'admin' && !window.confirm(`Make ${who} an admin? They'll be able to see everything and manage the team.`)) return
    setNotice('')
    setError('')
    try {
      await api('/api/admin/users/role', { method: 'PUT', body: JSON.stringify({ email: user.email, role }) })
      setNotice(`${who} is now an ${role}.`)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change that role.')
    }
  }

  async function remove(user: AdminUser) {
    if (user.id == null || !window.confirm(`Remove ${user.email}? They'll be signed out straight away.`)) return
    setNotice('')
    setError('')
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'DELETE' })
      setNotice(`Removed ${user.email}.`)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that admin.')
    }
  }

  const counts = { all: users?.length ?? 0, admin: 0, editor: 0 }
  for (const user of users ?? []) counts[user.role] += 1
  const needle = query.trim().toLowerCase()
  const shown = (users ?? []).filter(
    (user) =>
      (filter === 'all' || user.role === filter) &&
      (!needle || user.email.includes(needle) || user.name.toLowerCase().includes(needle)),
  )
  const pages = Math.max(1, Math.ceil(shown.length / PER_PAGE))
  const current = Math.min(page, pages)
  const visible = shown.slice((current - 1) * PER_PAGE, current * PER_PAGE)

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-caps text-[10px] text-rose">People</p>
          <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">Team</h2>
        </div>
        <button
          type="button"
          onClick={() => setAdding((open) => !open)}
          className="text-caps inline-flex h-9 items-center bg-ink px-4 text-white hover:bg-rose"
        >
          {adding ? 'Close' : 'Add someone'}
        </button>
      </div>
      <p className="mt-3 max-w-[60ch] text-sm text-muted">
        Admins can do everything; editors can write, edit and schedule posts. Switch anyone between admin and editor
        here, set a new password for someone who forgot theirs, or remove them.
      </p>

      {adding ? (
        <AddAdmin
          onAdded={(email) => {
            setNotice(`Added ${email}. Send them their password.`)
            setAdding(false)
            load()
          }}
        />
      ) : null}

      {notice ? <p className="mt-6 text-sm text-ink">{notice}</p> : null}
      {error ? <p className="mt-6 text-sm text-rose">{error}</p> : null}
      {!users && !error ? <p className="mt-6 text-muted">Loading the team…</p> : null}

      {users ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by role">
              {FILTERS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === item.key}
                  onClick={() => {
                    setFilter(item.key)
                    setPage(1)
                  }}
                  className={`text-caps h-8 border px-3 text-[10px] transition-colors ${
                    filter === item.key ? 'border-ink bg-ink text-white' : 'border-rule text-muted hover:border-ink hover:text-ink'
                  }`}
                >
                  {item.label} · {counts[item.key]}
                </button>
              ))}
            </div>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setPage(1)
              }}
              placeholder="Search name or email"
              aria-label="Search the team"
              className="h-8 w-full border border-rule bg-paper px-3 text-[13px] outline-none focus:border-ink sm:w-64"
            />
          </div>

          {visible.length === 0 ? (
            <p className="mt-4 text-sm text-muted">Nobody matches.</p>
          ) : (
            <ul className="mt-4 divide-y divide-rule border-y border-rule">
              {visible.map((user) => (
                <li key={user.email} className="py-1.5">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    <div
                      className="min-w-0 flex-1 basis-56"
                      title={user.owner ? 'Owner (set in Railway)' : user.createdBy ? `Added by ${user.createdBy}` : undefined}
                    >
                      <p className="truncate text-[14px] leading-snug">
                        {user.name || user.email}
                        {user.you ? <span className="text-muted"> · you</span> : null}
                        {user.owner ? <span className="text-muted"> · owner</span> : null}
                        {user.name ? <span className="block text-[12px] text-muted sm:ml-2 sm:inline">{user.email}</span> : null}
                      </p>
                    </div>
                    <RoleSwitch user={user} onChange={(role) => void changeRole(user, role)} />
                    <div className="flex w-36 shrink-0 justify-end gap-4">
                      {user.you ? null : (
                        <button
                          type="button"
                          className={linkButton}
                          onClick={() => {
                            setNotice('')
                            setResetting(resetting === user.email ? null : user.email)
                          }}
                        >
                          {resetting === user.email ? 'Cancel' : 'Password'}
                        </button>
                      )}
                      {user.you || user.owner ? null : (
                        <button type="button" className={linkButton} onClick={() => void remove(user)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {resetting === user.email ? (
                    <ResetPassword
                      user={user}
                      onDone={(message) => {
                        setResetting(null)
                        setNotice(message)
                      }}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <Pager page={current} pages={pages} onPage={setPage} />
        </>
      ) : null}
    </section>
  )
}
